import { AxiosResponse } from 'axios';
import Compressorjs from 'compressorjs';
import pureRequest from '@medlinker/apitool/request';
import { useEffect, useState, useRef } from 'react';
import request from '../request';
import env from './env';

let TOKEN_URL = '//med-files-dev.medlinker.com/upload/token';

if (env.appEnv === 'prod') {
  TOKEN_URL = 'https://med-files.medlinker.com/upload/token';
} else if (env.appEnv === 'qa') {
  TOKEN_URL = 'https://med-files-qa.medlinker.com/upload/token';
}
export type CompressOptions = Omit<Compressorjs.Options, 'success' | 'error'>;

interface QiniuResponse {
  w: number;
  h: number;
  key: string;
}

export interface UploadFileResult extends QiniuResponse {
  domain: string;
  fullUrl: string;
  file: File;
}

export interface UploadFileConfig {
  scope: string;
  watermark?: boolean;
  policy?: string;
  compress?: CompressOptions;
}

/**
 * 生成七牛裁剪图片地址
 * 更多信息：https://developer.qiniu.com/dora/manual/1279/basic-processing-images-imageview2
 * @param {string} url 七牛图片地址
 * @param {number} h 高
 * @param {number} w 宽
 * @param {0|1|2|3|4} mode 模式
 * @return {string}
 */
export function getQiniuImageCompressedUrl(
  url: string,
  h: number,
  w: number,
  mode: 0 | 1 | 2 | 3 | 4 = 0,
  format = true
) {
  if (!url) return '';

  let imgUrl = '';
  const splited = url.split('?');

  const compressAddon = `imageView2/${mode}/w/${w.toFixed(0)}/h/${h.toFixed(
    0
  )}${format ? '/format/jpg' : ''}`;

  if (splited[1]) {
    imgUrl = `${url}&${compressAddon}`;
  } else {
    imgUrl = `${splited[0]}?${compressAddon}`;
  }

  return imgUrl;
}

/**
 * 压缩图片
 * @param file input获取到的文件
 */
export function compressFile(file: File) {
  return new Promise<File>((resolve, reject) => {
    try {
      const fileObj = file;
      const reader = new FileReader();

      reader.readAsDataURL(fileObj); // 转base64
      reader.onload = (e: any) => {
        const image = new Image(); // 新建一个img标签（还没嵌入DOM节点)

        image.src = e.target.result;
        image.onload = () => {
          const canvas = document.createElement('canvas'); // 新建canvas
          const context = canvas.getContext('2d');
          const imageWidth = image.width / 2; // 压缩后图片的大小
          const imageHeight = image.height / 2;
          let data = '';

          canvas.width = imageWidth;
          canvas.height = imageHeight;
          context!.drawImage(image, 0, 0, imageWidth, imageHeight);
          data = canvas.toDataURL('image/jpeg'); // 输出压缩后的base64
          const arr = data.split(',');
          const mime = arr[0]!.match(/:(.*?);/)![1]; // 转成blob
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);

          // eslint-disable-next-line no-plusplus
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          const files = new window.File(
            [new Blob([u8arr], { type: mime })],
            'test.jpeg',
            {
              type: 'image/jpeg',
            }
          ); // 转成file

          resolve(files);
        };
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 上传图片到七牛，目前支持单张图片上传
 * TODO: 支持多张上传
 */
export function uploadFileToQiniu(file: File, config: UploadFileConfig) {
  return new Promise<UploadFileResult>((resolve, reject) => {
    request
      .post<{}, { token: string; domain: string }>(TOKEN_URL, {
        data: config,
      })
      .then(({ token, domain }) => {
        return new Compressorjs(file, {
          quality: 0.5,
          ...config.compress,
          async success(resultFile) {
            const fd = new FormData();
            const url = URL.createObjectURL(resultFile);

            fd.append('key', url.slice(url.lastIndexOf('/') + 1));
            fd.append('token', token);
            fd.append('file', resultFile);

            try {
              const res = await pureRequest.post<
                {},
                AxiosResponse<QiniuResponse>
              >('https://upload.qiniup.com', {
                data: fd,
              });

              resolve({
                ...res.data,
                domain,
                fullUrl: `${domain}/${res.data.key}`,
                file,
              });
            } catch (e) {
              reject(e);
            }
          },
          error(e) {
            reject(e);
          },
        });
      })
      .catch(reject);
  });
}

/**
 * 目前仅用于一张图片上传的情况
 * 后传的图片优先
 * 如果在上传过程中卸载，上传不会触发回调
 */
export function useUploader() {
  const [result, setResult] = useState<UploadFileResult | null>();
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const index = useRef<number>(1);

  useEffect(() => {
    return () => {
      index.current += 1;
    };
  }, []);

  return {
    result,
    error,
    isLoading,
    reset() {
      setError(null);
      setResult(null);
      index.current += 1;
    },
    upload(file: File, config: UploadFileConfig) {
      setError(null);
      setIsLoading(true);
      const cIndex = index.current;

      uploadFileToQiniu(file, config).then(
        res => {
          if (cIndex === index.current) {
            setResult(res);
            setIsLoading(false);
          }
        },
        err => {
          if (cIndex === index.current) {
            setError(err);
            setIsLoading(false);
          }
        }
      );
    },
  };
}
